import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Chip,
  Card,
  Divider,
  IconButton
} from '@mui/material';
import {
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  AdminPanelSettings as AdminIcon,
  Storefront as StorefrontIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Lock as LockIcon,
  VpnKey as KeyIcon,
  Security as SecurityIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { Role } from '../types';
import { getHierarchyInfo } from '../scopesConfig';

interface RoleSimulatorPreviewProps {
  open: boolean;
  role: Role | null;
  onClose: () => void;
}

export const RoleSimulatorPreview: React.FC<RoleSimulatorPreviewProps> = ({
  open,
  role,
  onClose
}) => {
  if (!role) return null;

  const hierarchy = getHierarchyInfo(role.level);
  const perms = role.permissions || {};
  const isAdminInterface = role.targetInterface === 'admin';

  // Анализ прав для модуля "Пользователи"
  const viewScope = perms['users.view'] || (role.level >= 100 ? 'all' : 'none');
  const canViewIin = role.level >= 100 || Boolean(perms['users.view_iin']);
  const canCreateUsers = role.level >= 100 || Boolean(perms['users.create']);
  const passwordScope = perms['users.edit_password'] || (role.level >= 100 ? 'all_in_list' : 'none');
  const canEditRole = role.level >= 100 || Boolean(perms['users.edit_role']);
  const statusScope = perms['users.toggle_status'] || (role.level >= 100 ? 'all_in_list' : 'none');
  const impersonateScope = perms['users.impersonate'] || (role.level >= 100 ? 'all_in_list' : 'none');
  const canDeleteUsers = role.level >= 100;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          px: 3,
          py: 2,
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2,
              backgroundColor: '#eff6ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#2563eb'
            }}
          >
            <VisibilityIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#0f172a' }}>
              Симулятор прав: {role.name}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              Интерактивный аудит видимости интерфейса и доступных операций
            </Typography>
          </Box>
        </Box>

        <IconButton onClick={onClose} sx={{ color: '#94a3b8', '&:hover': { color: '#0f172a', backgroundColor: '#f1f5f9' } }}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, backgroundColor: '#f8fafc' }}>
        {/* Карточка профиля роли */}
        <Card sx={{ p: 2.5, mb: 3, borderRadius: 2.5, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Chip
                label={role.name}
                sx={{
                  backgroundColor: role.badge.bg,
                  color: role.badge.text,
                  border: `1px solid ${role.badge.border}`,
                  fontWeight: 800,
                  fontSize: '0.95rem',
                  py: 2
                }}
              />
              <Chip
                label={`Ранг ${role.level}`}
                sx={{
                  backgroundColor: hierarchy.color,
                  color: '#fff',
                  fontWeight: 700
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                Рабочий интерфейс:
              </Typography>
              <Chip
                icon={isAdminInterface ? <AdminIcon sx={{ fontSize: '16px !important' }} /> : <StorefrontIcon sx={{ fontSize: '16px !important' }} />}
                label={isAdminInterface ? 'Админ-панель (/admin)' : 'Кабинет Аптеки (/dashboard)'}
                sx={{
                  backgroundColor: isAdminInterface ? '#eff6ff' : '#ecfdf5',
                  color: isAdminInterface ? '#1d4ed8' : '#047857',
                  border: `1px solid ${isAdminInterface ? '#bfdbfe' : '#a7f3d0'}`,
                  fontWeight: 700
                }}
              />
            </Box>
          </Box>

          <Typography variant="body2" sx={{ color: '#475569', mt: 2 }}>
            {role.description || 'Описание отсутствует'}
          </Typography>
        </Card>

        {/* Секция: Поведение в модуле "Пользователи и персонал" */}
        <Card sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1e293b', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon sx={{ color: '#3b82f6' }} />
            Симуляция в модуле «Пользователи»:
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            {/* 1. Область видимости списка */}
            <Box sx={{ p: 2, borderRadius: 2, backgroundColor: '#fff', border: '1px solid #f1f5f9' }}>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                Область просмотра пользователей:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.5 }}>
                {viewScope === 'all' && '🌐 Вся сеть аптек и офиса'}
                {viewScope === 'below_my_role' && `📉 Только ниже ранга ${role.level}`}
                {viewScope === 'created_by_me' && '👤 Только созданные данным сотрудником'}
                {viewScope === 'assigned_pharmacies' && '🏬 Только курируемые аптеки'}
                {viewScope === 'own_region' && '📍 Только свой город/регион'}
                {viewScope === 'none' && '⛔ Доступ к списку закрыт'}
              </Typography>
            </Box>

            {/* 2. Просмотр реального ИИН */}
            <Box sx={{ p: 2, borderRadius: 2, backgroundColor: '#fff', border: '1px solid #f1f5f9' }}>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                Отображение ИИН в таблице:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                {canViewIin ? (
                  <>
                    <CheckIcon sx={{ color: '#10b981', fontSize: 20 }} />
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#047857' }}>
                      Открыт полностью (000111222333)
                    </Typography>
                  </>
                ) : (
                  <>
                    <LockIcon sx={{ color: '#d97706', fontSize: 18 }} />
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#b45309' }}>
                      Маскирован (0001********)
                    </Typography>
                  </>
                )}
              </Box>
            </Box>

            {/* 3. Создание пользователей */}
            <Box sx={{ p: 2, borderRadius: 2, backgroundColor: '#fff', border: '1px solid #f1f5f9' }}>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                Регистрация новых пользователей:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: canCreateUsers ? '#0f172a' : '#94a3b8', mt: 0.5 }}>
                {canCreateUsers ? `Разрешено (только роли ранга < ${role.level})` : 'Запрещено'}
              </Typography>
            </Box>

            {/* 4. Смена паролей */}
            <Box sx={{ p: 2, borderRadius: 2, backgroundColor: '#fff', border: '1px solid #f1f5f9' }}>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                Смена паролей сотрудникам:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: passwordScope !== 'none' ? '#0f172a' : '#ef4444', mt: 0.5 }}>
                {passwordScope === 'all_in_list' && 'Любым пользователям младшего ранга'}
                {passwordScope === 'created_by_me' && 'Только лично созданным сотрудникам'}
                {passwordScope === 'none' && 'Запрещено (кнопка заблокирована)'}
              </Typography>
            </Box>

            {/* 5. Вход под пользователем (Impersonate) */}
            <Box sx={{ p: 2, borderRadius: 2, backgroundColor: '#fff', border: '1px solid #f1f5f9' }}>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                Вход под пользователем (Impersonate):
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: impersonateScope !== 'none' ? '#2563eb' : '#94a3b8', mt: 0.5 }}>
                {impersonateScope === 'all_in_list' && `Доступен для всех с рангом < ${role.level}`}
                {impersonateScope === 'created_by_me' && 'Только для созданных собой сотрудников'}
                {impersonateScope === 'none' && 'Функция отключена'}
              </Typography>
            </Box>

            {/* 6. Удаление пользователей */}
            <Box sx={{ p: 2, borderRadius: 2, backgroundColor: '#fff', border: '1px solid #f1f5f9' }}>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                Полное удаление аккаунтов:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                {canDeleteUsers ? (
                  <Typography variant="body2" sx={{ fontWeight: 800, color: '#dc2626' }}>
                    Разрешено (Главный Администратор)
                  </Typography>
                ) : (
                  <>
                    <LockIcon sx={{ color: '#64748b', fontSize: 18 }} />
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#64748b' }}>
                      Строго заблокировано (требуется ранг 100)
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Card>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
        <Button onClick={onClose} variant="contained" sx={{ backgroundColor: '#0f172a', fontWeight: 700 }}>
          Закрыть симулятор
        </Button>
      </DialogActions>
    </Dialog>
  );
};
